import React, {useState} from 'react';
import {Link} from 'react-router';
import {useSelector} from 'react-redux';
import {Tooltip} from 'react-tooltip';
import {refundBooking, updateBookings} from "../../actions/cartActions";
import Modal from "../Modal";
import TextInputWithLabel from "../TextInputWithLabel";
import ModalFooter from "../ModalFooter";
import ThemableSvgIcon from "../theme/ThemableSvgIcon";
import CartElementImage from "./CartElementImage";
import { Trans, useLingui } from '@lingui/react/macro';

function CartElementBooking(props) {
    const [ showRefundModal, setShowRefundModal ] = useState(false);
    const [ showConfirmationModal, setShowConfirmationModal ] = useState(false);
    const [ refundInProgress, setRefundInProgress ] = useState(false);
    const [ refundReference, setRefundReference ] = useState("");

    const {t} = useLingui();

    const sessionType = useSelector(state => state.authentication.sessionType);
    const ignoreAgency = useSelector(state => state.authentication.ignoreAgency);
    const isAgency = sessionType === "SingleSignOn" && !ignoreAgency;

    const processRefund = async () => {
        if(refundInProgress) {
            return;
        }

        setRefundInProgress(true);

        await refundBooking(props.refundIds, refundReference);

        setShowRefundModal(false);
        setRefundInProgress(false);
        setShowConfirmationModal(false);
        setRefundReference("");

        await updateBookings();
    };

    const handleRefundClick = () => {
        if(isAgency) {
            processRefund();
        }
        else {
            setShowRefundModal(false);
            setShowConfirmationModal(true);
        }
    };

    const isButtonDisabled = () => {
        // parent component can disable refund button first
        if(props.refundDisabled) {
            return true;
        }

        // agency needs to provide refund reference
        if(isAgency) {
            return !refundReference;
        }
        else {
            return false;
        }
    };

    return (
        <div>
            <div className="flex flex-wrap bg-primary-lightest shadow sm:p-4 my-4">
                {/* image */}
                <CartElementImage name={props.name} imageReference={props.imageReference} imageCategory={props.imageCategory} />

                {/* rest of card */}
                <div className={"sm:flex sm:flex-col px-4 py-2 sm:p-0 w-full sm:w-1/2 md:w-3/5 xl:w-2/3 mt-2 sm:mt-0 relative sm:grow xl:grow-0 xl:pl-4"} >
                    {/* Hide Cancel button for Excursions Included in the package */}
                    {/* Remove the booking - show refund modal */}
                    {!props.isIncludedExcursion && (
                        <span className="text-primary cursor-pointer absolute top-0 right-0 mr-4 mt-2 sm:m-0"
                                onClick={() => {setShowRefundModal(true)}}>
                            <span className="hidden md:inline">
                                <Trans id="cancel">Cancel</Trans>    
                            </span> 
                            <ThemableSvgIcon name="x" className="w-3 h-3 inline ml-1"/>
                        </span>
                    )}
                    
                    {/* title link */}
                    <div className="mr-6 md:mr-16">
                        {/* linkTo can be a plain string (Excursions) or an object {pathname, state} (TAC) */}
                        <Link 
                            className="text-primary underline text-2xl uppercase sm:pr-3 md:pr-0" 
                            to={typeof props.linkTo === 'string' ? props.linkTo : props.linkTo.pathname}
                            state={typeof props.linkTo === 'string' ? undefined : props.linkTo.state}
                        >
                            {props.name}
                        </Link>
                    </div>

                    {/* details */}
                    <div className="sm:grow">
                        <div className="my-2">
                            {props.children}
                        </div>
                    </div>
                    {/* cost info */}
                    {props.isIncludedExcursion ? (
                        <div className="text-right">
                            Included
                        </div>
                    ) : (
                        <div className="text-right">
                            {props.state === "Waitlisted" ? t({id: "waitlist_confirmed"}) : t({id: "reserved"})}
                            {" -"}
                            <span
                                className="cursor-pointer ml-2 text-primary border-dotted border-b border-primary"
                                data-tooltip-content={props.tooltipText}
                                data-tooltip-id={props.tooltipId}
                                data-effect="solid"
                                data-place="bottom"
                                data-multiline={true}
                            >
                                {props.state === "Waitlisted" ? t({id: "note"}) : t({id: "see_cost"})}
                            </span>
                        </div>
                    )}

                    <Tooltip id={props.tooltipId}/>
                </div>
            </div>

            {/* Refund modal */}
            <Modal
                show={showRefundModal}
                onClose={() => {setShowRefundModal(false)}}
                title={t({id: "cancel_reservation"})}
            >
                <div className="flex grow flex-col">
                    <div className="grow bg-white py-4 px-2 sm:px-4">
                        <div>
                            {props.modalContent}

                            {isAgency &&
                            <div className="my-4">
                                <TextInputWithLabel
                                    label={t({id: "refund_reference"})}
                                    type="text"
                                    value={refundReference}
                                    _onChange={value => setRefundReference(value)}
                                    placeholder=""
                                    errorMessage=""
                                />
                            </div>
                            }
                        </div>
                    </div>
                    <ModalFooter
                        actionDisabled={isButtonDisabled() || refundInProgress}
                        actionButtonText={refundInProgress ? `${t({id: "please_wait"})}...` : t({id: "cancel_reservation"})}
                        onActionClick={handleRefundClick}
                    />
                </div>
            </Modal>


            {/* Confirmation modal */}
            <Modal
                show={showConfirmationModal}
                onClose={() => {setShowConfirmationModal(false)}}
                title={t({id: "confirmation"})}
            >
                <div className="flex grow flex-col">
                    <div className="grow bg-white py-4 px-2 sm:px-4">
                        <div className="text-xl font-bold">
                            <Trans id="are_you_sure_to_cancel_reservation">Are you sure you want to cancel this reservation</Trans>
                        </div>
                    </div>

                    <ModalFooter
                        cancelLabel={t({id: "no"})}
                        onCancel={() => {setShowConfirmationModal(false)}}
                        actionDisabled={refundInProgress}
                        actionButtonText={refundInProgress ? `${t({id: "please_wait"})}...` : t({id: "yes_cancel"})}
                        onActionClick={processRefund}
                    />
                </div>
            </Modal>
        </div>
    )
}

export default CartElementBooking;
